<!--
  Copyright (C) 2022-2025 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <div class="chat-room-list">
    <!-- Search -->
    <div class="chat-room-list__search">
      <span class="p-input-icon-left w-full">
        <i class="ri-search-line" aria-hidden="true"></i>
        <PrimeInputText
          v-model="searchQuery"
          :placeholder="$t('chat.searchRooms')"
          class="w-full"
        />
      </span>
    </div>

    <!-- Room list -->
    <div class="chat-room-list__items">
      <div
        v-for="room in filteredRooms"
        :key="room._id"
        class="chat-room-item"
        :class="{ 'chat-room-item--selected': room._id === selectedRoomId }"
        @click="selectRoom(room)"
      >
        <!-- Room icon -->
        <div class="chat-room-item__icon">
          <i :class="getRoomIcon(room)" aria-hidden="true"></i>
        </div>

        <!-- Room info -->
        <div class="chat-room-item__info">
          <div class="chat-room-item__name">
            {{ room.fname || room.name }}
          </div>
          <div v-if="room.lastMessage" class="chat-room-item__last-message">
            <span v-if="room.lastMessage.u" class="last-message-user">
              {{ room.lastMessage.u.name || room.lastMessage.u.username }}:
            </span>
            {{ truncateMessage(room.lastMessage.msg) }}
          </div>
        </div>

        <!-- Unread badge -->
        <PrimeBadge
          v-if="getUnreadCount(room._id) > 0"
          :value="getUnreadCount(room._id)"
          severity="danger"
          class="chat-room-item__badge"
        />
      </div>

      <!-- Empty state -->
      <div v-if="filteredRooms.length === 0" class="chat-room-list__empty">
        <i class="ri-chat-3-line" aria-hidden="true"></i>
        <p>{{ $t("chat.noRooms") }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import InputText from "primevue/inputtext";
import Badge from "primevue/badge";

export default {
  name: "ChatRoomList",
  components: {
    PrimeInputText: InputText,
    PrimeBadge: Badge,
  },
  props: {
    rooms: {
      type: Array,
      default: () => [],
    },
    selectedRoomId: {
      type: String,
      default: null,
    },
    unreadCounts: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["select"],
  data() {
    return {
      searchQuery: "",
    };
  },
  computed: {
    filteredRooms() {
      if (!this.searchQuery.trim()) {
        return this.sortedRooms;
      }

      const query = this.searchQuery.toLowerCase();
      return this.sortedRooms.filter((room) => {
        const name = (room.fname || room.name || "").toLowerCase();
        return name.includes(query);
      });
    },
    sortedRooms() {
      return [...this.rooms].sort((a, b) => {
        // Sort by unread count first
        const unreadA = this.unreadCounts[a._id] || 0;
        const unreadB = this.unreadCounts[b._id] || 0;
        if (unreadA !== unreadB) {
          return unreadB - unreadA;
        }

        // Then by last activity
        const dateA = new Date(a.updatedAt || 0);
        const dateB = new Date(b.updatedAt || 0);
        return dateB - dateA;
      });
    },
  },
  methods: {
    selectRoom(room) {
      this.$emit("select", room._id);
    },
    getUnreadCount(roomId) {
      return this.unreadCounts[roomId] || 0;
    },
    getRoomIcon(room) {
      switch (room.type) {
        case "c":
          return "ri-hashtag"; // Public channel
        case "p":
          return "ri-lock-line"; // Private group
        case "d":
          return "ri-user-line"; // Direct message
        default:
          return "ri-chat-3-line";
      }
    },
    truncateMessage(msg) {
      if (!msg) {
        return "";
      }
      const maxLength = 40;
      if (msg.length <= maxLength) {
        return msg;
      }
      return msg.substring(0, maxLength) + "...";
    },
  },
};
</script>

<style lang="css" scoped>
.chat-room-list {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--p-surface-ground);
}

.chat-room-list__search {
  padding: 0.75rem;
  border-bottom: 1px solid var(--p-surface-border);
}

.chat-room-list__items {
  flex: 1;
  overflow-y: auto;
}

.chat-room-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  cursor: pointer;
  transition: background-color 0.15s;
}

.chat-room-item:hover {
  background-color: var(--surface-hover);
}

.chat-room-item--selected {
  background-color: var(--primary-50);
}

.chat-room-item__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  background-color: var(--surface-200);
  border-radius: 50%;
  color: var(--text-color-secondary);
}

.chat-room-item--selected .chat-room-item__icon {
  background-color: var(--primary-100);
  color: var(--primary-color);
}

.chat-room-item__info {
  flex: 1;
  min-width: 0;
}

.chat-room-item__name {
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-room-item__last-message {
  font-size: 0.8rem;
  color: var(--text-color-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.last-message-user {
  font-weight: 500;
}

.chat-room-item__badge {
  flex-shrink: 0;
}

.chat-room-list__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: var(--text-color-secondary);
}

.chat-room-list__empty i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}
</style>
